import React, { FC } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { FEED_TOPICS } from '../constants/feedTopics';

/** Matches the server's MAX_COMMUNITY_TOPICS — the schema enforces it there. */
export const MAX_COMMUNITY_TOPICS = 3;

interface TopicPickerProps {
  value: string[];
  onChange: (topics: string[]) => void;
  /** Shown under the chips; defaults to the selection count. */
  helper?: string;
}

/**
 * Herd topic selector — the same chip idiom PreferencesPage uses for the
 * user's own topics, so picking topics feels identical on both sides of the
 * match. Capped at MAX_COMMUNITY_TOPICS: further chips disable rather than
 * silently dropping a pick at submit time.
 */
const TopicPicker: FC<TopicPickerProps> = ({ value, onChange, helper }) => {
  const toggle = (topic: string) => {
    onChange(
      value.includes(topic) ? value.filter((t) => t !== topic) : [...value, topic]
    );
  };

  const full = value.length >= MAX_COMMUNITY_TOPICS;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {FEED_TOPICS.map((topic) => {
          const isSelected = value.includes(topic);
          return (
            <Chip
              key={topic}
              label={topic}
              clickable
              disabled={!isSelected && full}
              color={isSelected ? 'primary' : 'default'}
              variant={isSelected ? 'filled' : 'outlined'}
              onClick={() => toggle(topic)}
            />
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {helper ??
          (value.length === 0
            ? `Pick up to ${MAX_COMMUNITY_TOPICS} topics so people who follow them can find this herd.`
            : `${value.length}/${MAX_COMMUNITY_TOPICS} selected`)}
      </Typography>
    </Box>
  );
};

export default TopicPicker;
